import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import {
  Banner,
  Breadcrumbs,
  LayerCard,
  SkeletonLine,
  Grid,
  GridItem,
  useKumoToastManager,
} from '@cloudflare/kumo'
import { HouseIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import {
  CredentialsCard,
  useRevealState,
  type WifiNetwork,
} from '../components/wifi/CredentialsCard'
import { RegenerateSection } from '../components/wifi/RegenerateSection'
import { SetupSteps } from '../components/wifi/SetupSteps'

type NetworkInfo = WifiNetwork

interface NetworksResponse {
  guests: NetworkInfo[]
  coworkers: NetworkInfo[]
}

export function WifiCredentialsPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const toasts = useKumoToastManager()
  const [networks, setNetworks] = useState<NetworksResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [regenerating, setRegenerating] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const [revealed, toggleReveal] = useRevealState()

  async function handleRegenerate() {
    setRegenerating(true)

    try {
      const token = await getAccessTokenSilently()
      const result = await apiRequest<{ password: string }>('/api/wifi/regenerate-password', {
        method: 'POST',
        token,
      })

      setNetworks((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          coworkers: prev.coworkers.map((n) =>
            n.securityType === 'WPA2/WPA3 Enterprise' && n.credential
              ? { ...n, credential: { ...n.credential, password: result.password } }
              : n,
          ),
        }
      })

      toasts.add({ variant: 'success', title: t('wifi.regenerateSuccess') })
      setConfirmRegenerate(false)
    } catch {
      toasts.add({
        variant: 'error',
        title: t('common.errorTitle'),
        description: t('wifi.regenerateError'),
      })
    } finally {
      setRegenerating(false)
    }
  }

  useEffect(() => {
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        await apiRequest('/api/auth/login-callback', { method: 'POST', token }).catch(() => {})
        const nets = await apiRequest<NetworksResponse>('/api/wifi/networks', {
          method: 'GET',
          token,
        })
        setNetworks(nets)
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          setError('NOT_ENROLLED')
        } else {
          setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
        }
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [getAccessTokenSilently])

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('wifi.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('wifi.title')}
        description={t('wifi.description')}
      />

      {loading && (
        <>
          <section className="flex flex-col gap-2 py-3">
            <Grid variant="2up" gap="base">
              {[1, 2].map((i) => (
                <GridItem key={i}>
                  <LayerCard>
                    <LayerCard.Primary>
                      <div className="space-y-3">
                        <div>
                          <SkeletonLine minWidth={15} maxWidth={20} className="h-3" />
                          <SkeletonLine minWidth={40} maxWidth={60} className="h-5" />
                        </div>
                        <div>
                          <SkeletonLine minWidth={15} maxWidth={20} className="h-3" />
                          <SkeletonLine minWidth={50} maxWidth={70} className="h-5" />
                        </div>
                        <div>
                          <SkeletonLine minWidth={15} maxWidth={20} className="h-3" />
                          <SkeletonLine minWidth={50} maxWidth={70} className="h-5" />
                        </div>
                      </div>
                    </LayerCard.Primary>
                  </LayerCard>
                </GridItem>
              ))}
            </Grid>
          </section>
        </>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')}{' '}
          {error === 'NOT_ENROLLED'
            ? t('common.notEnrolled')
            : error === 'GENERIC_ERROR'
              ? t('common.error')
              : error}
        </Banner>
      )}

      {!loading && !error && !networks && <Banner variant="error">{t('common.error')}</Banner>}

      {!loading &&
        !error &&
        networks &&
        networks.coworkers.length > 0 &&
        (() => {
          const enterprise = networks.coworkers.find(
            (n) => n.securityType === 'WPA2/WPA3 Enterprise' && n.credential,
          )
          return (
            <div className="flex flex-col gap-6">
              <Grid variant="2up" gap="base">
                <GridItem>
                  <div className="flex flex-col gap-3">
                    {networks.coworkers.map((n) => (
                      <div
                        key={n.name}
                        style={regenerating ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
                      >
                        <CredentialsCard
                          network={n}
                          revealed={revealed}
                          onToggleReveal={toggleReveal}
                        />
                      </div>
                    ))}
                  </div>
                </GridItem>
                {enterprise?.credential && (
                  <GridItem>
                    <SetupSteps
                      networkName={enterprise.name}
                      username={enterprise.credential.username}
                      password={enterprise.credential.password ?? '\u2014'}
                      revealed={revealed}
                    />
                  </GridItem>
                )}
              </Grid>
              {enterprise && (
                <RegenerateSection
                  regenerating={regenerating}
                  onRequest={() => setConfirmRegenerate(true)}
                />
              )}
            </div>
          )
        })()}

      <ConfirmDialog
        open={confirmRegenerate}
        onOpenChange={setConfirmRegenerate}
        title={t('wifi.confirmRegenerateTitle')}
        description={t('wifi.confirmRegenerate')}
        confirmLabel={t('wifi.confirmRegenerateAction')}
        loading={regenerating}
        onConfirm={() => void handleRegenerate()}
      />
    </div>
  )
}
